import {
    getApiErrorMessage,
    IApiAxiosError,
    IClientMinimal,
    ILibraryFolder,
    updateLibraryFolder,
} from "@/api";
import { ClientAutocomplete } from "@/components/shared/ClientAutocomplete";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface EditFolderDialogProps {
    open: boolean;
    onClose: () => void;
    folder: ILibraryFolder;
    onSaved: () => void;
}

export const EditFolderDialog = ({
    open,
    onClose,
    folder,
    onSaved,
}: EditFolderDialogProps) => {
    const queryClient = useQueryClient();
    const [name, setName] = useState(folder.name);
    const [selectedClient, setSelectedClient] = useState<IClientMinimal | null>(
        folder.client ?? null
    );
    const [error, setError] = useState<string | null>(null);

    // Reset on dialog (re-)open or when a different folder is being edited.
    // Keyed by `folder.id` (primitive) instead of `folder.name`/`folder.client`
    // (object refs) so an unrelated parent re-render — common: ActionsBar
    // synthesizes a fresh `folder` object every render, and React Query
    // refetches produce new identities — doesn't clobber the user's in-progress
    // edits mid-typing.
    useEffect(() => {
        if (open) {
            setName(folder.name);
            setSelectedClient(folder.client ?? null);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, folder.id]);

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            updateLibraryFolder(folder.id, {
                name: name.trim(),
                client_id: selectedClient?.id ?? null,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-list"],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-library-folders"],
            });
            queryClient.invalidateQueries({ queryKey: ["library-documents"] });

            toast.success("Folder updated successfully");
            handleClose();
            onSaved();
        },
        onError: (err: IApiAxiosError) => {
            setError(getApiErrorMessage(err, "Failed to update folder"));
        },
    });

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const handleSubmit = () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError("Folder name is required");
            return;
        }
        // Silent-close parity with rename dialogs: when nothing changed,
        // close without firing the mutation or a toast.
        const clientUnchanged =
            (selectedClient?.id ?? null) === (folder.client?.id ?? null);
        if (trimmedName === folder.name && clientUnchanged) {
            handleClose();
            return;
        }
        setError(null);
        mutate();
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Edit Folder</DialogTitle>
                    <DialogDescription>
                        Editing &quot;{folder.name}&quot;
                    </DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                    className="space-y-3"
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="edit-folder-name">Folder Name</Label>
                        <Input
                            id="edit-folder-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isPending}
                        />
                    </div>

                    <ClientAutocomplete
                        value={selectedClient}
                        onChange={setSelectedClient}
                        disabled={isPending}
                        label="Client (optional)"
                        helperText="Documents uploaded to this folder will inherit this client"
                    />

                    {error && (
                        <p className="text-destructive text-sm">{error}</p>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isPending || !name.trim()}
                        >
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
